// routes.js - The route table as plain data, so three things stay in sync
// automatically: the router, the prerenderer, and sitemap.xml.
//
// `prerender: false` means the page is generated per visitor and must never be
// baked into static HTML (a tracking page, the dashboard). `priority` and
// `changefreq` feed sitemap.xml.
//
// LEGEND
//   1. SITE_ROUTES   every public URL, with the one line summary llms.txt uses
//   2. NAV           the header, built from the routes above
//   3. FOOTER_NAV    the footer columns
//   4. sitemapUrls() what prerender.js writes into sitemap.xml
//
// `summary` is a sentence about the page for llms.txt, the plain text index an
// assistant reads instead of crawling. The legal pages have no summary here
// because theirs lives in the front matter of the markdown they render.

////////// 1. PUBLIC ROUTES //////////

export const SITE_ROUTES = [
  { path: "/", label: "Home", priority: 1.0, changefreq: "weekly", summary: "Sell gold, silver and platinum by mail. Free insured kit, every package opened on camera, itemised offer, free return if you decline." },
  { path: "/how-it-works", label: "How it works", priority: 0.9, changefreq: "monthly", summary: "The three steps end to end: request a kit, post your items, review the itemised offer and decide." },
  { path: "/what-we-buy", label: "What we buy", priority: 0.9, changefreq: "monthly", summary: "Everything we buy and everything we do not, including broken chains, single earrings, sterling flatware, dental gold and bullion." },
  { path: "/why-us", label: "Why us", priority: 0.7, changefreq: "monthly", summary: "What we do differently: every weight and purity test published, opened on camera, and free insured return." },
  { path: "/sell-scrap-gold", label: "Sell scrap gold", priority: 0.9, changefreq: "monthly", summary: "Sell scrap gold by mail: broken chains, single earrings, class rings, worn bands and dental gold, weighed and tested on camera and priced on verified metal content." },
  { path: "/get-a-kit", label: "Get a free kit", priority: 1.0, changefreq: "monthly", summary: "Request the free insured mail-in kit. Three short screens, no obligation." },
  // Prerendered so it loads instantly, kept out of the sitemap because a thank
  // you page in a search result is somebody landing on a receipt for something
  // they never did. Google Ads counts a URL without needing it indexed.
  { path: "/get-a-kit/confirmed", label: "Request received", index: false },

  { path: "/prices", label: "Live metal prices", priority: 0.8, changefreq: "hourly", summary: "Live gold, silver, platinum and palladium spot prices per troy ounce, per gram and per pennyweight." },
  { path: "/embed", label: "Free charts for your site", priority: 0.8, changefreq: "monthly", summary: "Free live metal price charts you can embed on your own site." },
  { path: "/price-alerts", label: "Free price alerts", priority: 0.7, changefreq: "monthly", summary: "Free email alerts when a metal price moves past a level you choose." },

  { path: "/tools", label: "Calculators", priority: 0.8, changefreq: "monthly", summary: "Free calculators for working out the precious metal content of a piece and what it is worth at spot." },
  { path: "/tools/gold-calculator", label: "Scrap gold calculator", priority: 0.9, changefreq: "monthly", summary: "Work out the pure gold content and melt value of scrap gold by karat and weight." },
  { path: "/tools/silver-calculator", label: "Silver calculator", priority: 0.8, changefreq: "monthly", summary: "Work out the pure silver content and melt value of sterling, coin silver and bullion." },
  { path: "/tools/platinum-calculator", label: "Platinum calculator", priority: 0.7, changefreq: "monthly", summary: "Work out the pure platinum content and melt value by purity and weight." },
  { path: "/tools/karat-chart", label: "Karat and hallmark chart", priority: 0.8, changefreq: "monthly", summary: "What 417, 585, 750, 925, PLAT, GF and EPNS mean, with the pure metal content of each marking." },
  { path: "/tools/weight-converter", label: "Weight converter", priority: 0.6, changefreq: "monthly", summary: "Convert between grams, pennyweight, troy ounces and standard ounces." },

  { path: "/learn", label: "Learn", priority: 0.8, changefreq: "weekly", summary: "Plain guides to selling gold and silver, written by the people who weigh it." },
  { path: "/faq", label: "Questions", priority: 0.8, changefreq: "monthly", summary: "The questions people actually ask about mailing in precious metals, answered including the inconvenient parts." },
  { path: "/work-with-us", label: "Work with us", priority: 0.8, changefreq: "monthly", summary: "Earn a commission for referring gold and silver sellers. A free countertop display, free kits and free restocks." },
  { path: "/about", label: "About us", priority: 0.6, changefreq: "yearly", summary: "Who we are, where we are, and how we decided to run this business." },
  { path: "/contact", label: "Contact", priority: 0.6, changefreq: "yearly", summary: "Phone, email and postal address, and the hours a person answers." },

  { path: "/shows", label: "Gold shows", priority: 0.8, changefreq: "weekly", summary: "Where to find us in person. Buying events across the country, with free appraisals while you wait." },
  // A show lives in the database and the prerenderer runs at build time with no
  // database, so this one is rendered in the browser like the tracking page.
  { path: "/shows/:slug", label: "Gold show", prerender: false, index: false },

  { path: "/track", label: "Track an order", priority: 0.5, changefreq: "yearly", summary: "Find an existing order using the link we sent, or recover it by email or text." },
  { path: "/track/:token", label: "Order status", prerender: false, index: false },

  { path: "/legal", label: "Legal", priority: 0.3, changefreq: "yearly", summary: "Every document we ask a customer to agree to, published in full, dated and versioned." },
  // Deliberately not in the header and not on the home page. Almost nobody needs
  // more than the standard cover, and putting a form about insurance in front of
  // every visitor is a decision they should not have to make. It lives in the
  // footer, in the shipping policy, and in the answer people find when they go
  // looking for it.
  { path: "/extra-coverage", label: "Higher value cover", priority: 0.3, changefreq: "yearly", summary: "Request shipping cover above the standard insured value before you post high value items." },

  { path: "/legal/terms", label: "Terms and conditions", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/privacy", label: "Privacy policy", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/shipping", label: "Shipping, insurance and returns", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/purchase-agreement", label: "Purchase agreement", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/aml", label: "Anti money laundering policy", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/sms", label: "Text message terms", priority: 0.2, changefreq: "yearly" },
  { path: "/legal/accessibility", label: "Accessibility statement", priority: 0.2, changefreq: "yearly" },
  // Prerendered so an affiliate can be sent a real link, and kept out of the
  // sitemap and the index so a customer is never shown it.
  { path: "/legal/affiliate-terms", label: "Affiliate terms", index: false },
  // The application. It used to be an address to send somebody and nothing
  // that led to it, on the reasoning that a customer should never wander into
  // a page about commission. That has been turned around: the programme is a
  // public avenue now, /work-with-us leads straight here, and a search for
  // "buy gold affiliate program" landing on it is a shop we did not have to go
  // and find.
  { path: "/affiliate-signup", label: "Affiliate application", priority: 0.6, changefreq: "monthly" },
  // Their own page. Real enough to be sent a link to, and never indexed: a
  // search result for it is a stranger landing on somebody else's earnings.
  { path: "/affiliate-portal", label: "Affiliate page", index: false },
];

////////// 2. HEADER //////////
// Four items and one call to action. Anything more and nobody reads any of them.

export const NAV = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/what-we-buy", label: "What we buy" },
  { to: "/tools", label: "Calculators" },
  { to: "/learn", label: "Learn" },
  { to: "/work-with-us", label: "Work with us" },
];

////////// 3. FOOTER //////////

export const FOOTER_NAV = [
  {
    title: "Selling",
    links: [
      { to: "/get-a-kit", label: "Get a free kit" },
      { to: "/how-it-works", label: "How it works" },
      { to: "/what-we-buy", label: "What we buy" },
      { to: "/sell-scrap-gold", label: "Sell scrap gold" },
      { to: "/why-us", label: "Why sell to us" },
      { to: "/shows", label: "Gold shows" },
      { to: "/track", label: "Track an order" },
    ],
  },
  {
    title: "Tools",
    links: [
      { to: "/prices", label: "Live metal prices" },
      { to: "/price-alerts", label: "Free price alerts" },
      { to: "/embed", label: "Charts for your site" },
      { to: "/tools/gold-calculator", label: "Scrap gold calculator" },
      { to: "/tools/silver-calculator", label: "Silver calculator" },
      { to: "/tools/platinum-calculator", label: "Platinum calculator" },
      { to: "/tools/karat-chart", label: "Karat and hallmark chart" },
      { to: "/tools/weight-converter", label: "Weight converter" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/work-with-us", label: "Work with us" },
      { to: "/about", label: "About us" },
      { to: "/learn", label: "Articles" },
      { to: "/faq", label: "Questions" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/legal/terms", label: "Terms and conditions" },
      { to: "/legal/privacy", label: "Privacy policy" },
      { to: "/legal/privacy#advertising-switch", label: "Do not share my info" },
      { to: "/legal/shipping", label: "Shipping and returns" },
      { to: "/extra-coverage", label: "Higher value cover" },
      { to: "/legal/purchase-agreement", label: "Purchase agreement" },
      { to: "/legal/aml", label: "AML policy" },
      { to: "/legal/sms", label: "Text message terms" },
      { to: "/legal/accessibility", label: "Accessibility" },
    ],
  },
];

////////// 4. SITEMAP //////////

// `lastmod` is the one field here Google actually uses, and only while it is
// believed. Stamping every URL with today's date on every deploy is what makes
// it stop being believed, so a date is emitted only where a real one exists:
// the front matter of an article, the effective date of a legal document, or a
// `changed` on the route itself. Everything else omits the field rather than
// inventing one, which is the honest answer and the one that keeps the dates
// that are real worth reading.
//
// `dated` maps a path to its real date and is supplied by prerender.js, which is
// the only place that can read the content files.

export function sitemapUrls(origin, articleSlugs = [], dated = {}) {
  const entry = (path, priority, changefreq) => {
    const lastmod = dated[path] || null;
    return { loc: `${origin}${path}`, priority, changefreq, ...(lastmod ? { lastmod } : {}) };
  };

  const pages = SITE_ROUTES
    .filter((route) => route.prerender !== false && route.index !== false)
    .map((route) => entry(route.path, route.priority ?? 0.5, route.changefreq || "monthly"));

  const articles = articleSlugs.map((slug) => entry(`/learn/${slug}`, 0.7, "yearly"));

  return [...pages, ...articles];
}

// The concrete paths the prerenderer walks. Dynamic segments are excluded.
export const prerenderPaths = (articleSlugs = []) => [
  ...SITE_ROUTES.filter((route) => route.prerender !== false && !route.path.includes(":")).map((route) => route.path),
  ...articleSlugs.map((slug) => `/learn/${slug}`),
];
